import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { InferType, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { t } from "i18next";
import { tv } from "tailwind-variants";
import { H3, P } from "@/components/Typography";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import {
  useConsentFormFields,
  usePostPrivacyEmail,
} from "@/requests/hooks/collect.ts";

const styles = tv({
  slots: {
    titleAndDescriptionContainer: "mb-20",
    title: "mb-10 font-bold",
    buttonContainer: "my-20 md:my-45",
  },
});

declare interface IProps {
  privacyEmail?: string | null;
  version?: number;
  isDataFetching: boolean;
  isPostPrivacyPolicyUrlPending: boolean;
  postPrivacyEmailHook: ReturnType<typeof usePostPrivacyEmail>;
  refetchConsentFormFields: ReturnType<typeof useConsentFormFields>["refetch"];
}

const PrivacyEmailForm = ({
  privacyEmail,
  version,
  isDataFetching,
  isPostPrivacyPolicyUrlPending,
  postPrivacyEmailHook,
  refetchConsentFormFields,
}: IProps) => {
  const { titleAndDescriptionContainer, title, buttonContainer } = styles();

  const formSchema = object({
    email: string()
      .email()
      .required(t("settings.widgetSettings.privacyDetails.email.error"))
      .nullable(),
  });

  type IPrivacyEmailForm = InferType<typeof formSchema>;

  const {
    mutate: postPrivacyEmail,
    isPending: isPostPrivacyEmailPending,
    isSuccess: isPostPrivacyEmailSuccess,
    data: postPrivacyEmailData,
  } = postPrivacyEmailHook;

  const {
    watch,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<IPrivacyEmailForm>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      email: privacyEmail,
    },
  });

  const handleOnSubmitPostPrivacyEmail = ({ email }: IPrivacyEmailForm) => {
    postPrivacyEmail({
      privacyEmail: email,
      version: Math.max(postPrivacyEmailData?.version ?? 0, version ?? 0),
    });
  };

  useEffect(() => {
    if (isPostPrivacyEmailSuccess) {
      toast.success(t("settings.widgetSettings.privacyDetails.email.success"));
      refetchConsentFormFields();
    }
  }, [isPostPrivacyEmailSuccess]);

  const shouldButtonBeDisabled = () =>
    isDataFetching ||
    isPostPrivacyEmailPending ||
    isPostPrivacyPolicyUrlPending ||
    (postPrivacyEmailData?.privacyEmail ?? privacyEmail) === watch("email");

  const renderTitleAndDescriptionSection = () => (
    <div className={titleAndDescriptionContainer()}>
      <div className={title()}>
        <H3>{t("settings.widgetSettings.privacyDetails.email.title")}</H3>
      </div>
      <P>{t("settings.widgetSettings.privacyDetails.email.description")}</P>
    </div>
  );

  const renderPrivacyEmailInput = () => (
    <div>
      <Input
        label={t("settings.widgetSettings.privacyDetails.email.formLabel")}
        placeholder={t(
          "settings.widgetSettings.privacyDetails.email.placeholder",
        )}
        type="email"
        role="textbox"
        {...register("email")}
        helperText={errors?.email?.message}
        variant={!!errors?.email?.message ? "error" : undefined}
        helperTextTestId="privacy-email-input"
        testId="privacy-email-input"
        id="privacy-email-input"
      />
    </div>
  );

  const renderButton = () => (
    <div className={buttonContainer()}>
      <Button
        type="submit"
        loading={isPostPrivacyEmailPending}
        trackingId="control-panel.collect.widget-settings.privacy-details.update-email"
        disabled={shouldButtonBeDisabled()}
        testId="privacy-email-cta"
        id="privacy-email-cta">
        {t("settings.widgetSettings.privacyDetails.email.cta")}
      </Button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit(handleOnSubmitPostPrivacyEmail)}>
      {renderTitleAndDescriptionSection()}
      {renderPrivacyEmailInput()}
      {renderButton()}
    </form>
  );
};

export default PrivacyEmailForm;
